/**
 * Customer Login Page — Supabase Auth with Email & Phone SMS OTP Verification.
 * Supports bulletproof validation, state persistence, and redirect parameters.
 */

import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import {
  Lock,
  Mail,
  Phone,
  ArrowRight,
  ShieldCheck,
  AlertCircle,
  KeyRound,
  CheckCircle2,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { BreadcrumbNav } from "@/components/common/breadcrumb-nav";
import { siteConfig } from "@/config/site";
import { useCurrentUser } from "@/hooks/use-auth";

export const Route = createFileRoute("/account/login")({
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: (search.redirect as string) || undefined,
  }),
  head: () => ({
    meta: [
      { title: `Sign In — ${siteConfig.name}` },
      // noindex,follow — signed-in/auth surfaces carry no search value. Deliberately not a
      // robots.txt Disallow: /account/login is linked from the site header on every page,
      // and a disallowed URL is still eligible for URL-only indexing while Google never
      // fetches it and so never reads the directive that would remove it. Allowing the
      // crawl and serving noindex is the combination that actually de-indexes.
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: LoginPage,
});

export function LoginPage() {
  const { isAuthenticated } = useCurrentUser();
  const navigate = useNavigate();
  const searchParams = Route.useSearch();
  const redirectUrl = searchParams.redirect || "/account";

  const [authMethod, setAuthMethod] = useState<"EMAIL" | "PHONE">("EMAIL");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Phone OTP state
  const [phoneNum, setPhoneNum] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpNotice, setOtpNotice] = useState<string | null>(null);

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Auto-redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate({ to: redirectUrl as never });
    }
  }, [isAuthenticated, redirectUrl, navigate]);

  // Format phone number to E.164 standard
  const formatPhoneNumber = (input: string) => {
    let cleaned = input.trim().replace(/[^\d+]/g, "");
    if (!cleaned.startsWith("+")) {
      // Default to +91 if no country code provided
      cleaned = `+91${cleaned.replace(/^0+/, "")}`;
    }
    return cleaned;
  };

  const extractAuthErrorMessage = (error: unknown): string => {
    if (!error) return "Authentication failed. Please try again.";
    if (typeof error === "string" && error.trim() && error !== "{}") {
      return error;
    }
    const e = error as { message?: string; error_description?: string; error?: string };
    const msg = e.message || e.error_description || e.error || "";
    if (typeof msg === "string" && msg.trim() && msg !== "{}") {
      if (msg.includes("Email not confirmed")) {
        return "Please confirm your email address before signing in, or create a new account.";
      }
      if (msg.includes("User not found") || msg.includes("Invalid login credentials")) {
        return "Invalid email or password. Please check your credentials.";
      }
      return msg;
    }
    return "Invalid email or password. Please check your credentials.";
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg(null);

    if (!email || !password) {
      setErrorMsg("Please enter both your email address and password.");
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (error) {
        setErrorMsg(extractAuthErrorMessage(error));
      } else if (data.session) {
        navigate({ to: redirectUrl as never });
      }
    } catch (err: unknown) {
      setErrorMsg(extractAuthErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg(null);
    setOtpNotice(null);

    const formattedPhone = formatPhoneNumber(phoneNum);

    if (formattedPhone.length < 10) {
      setErrorMsg("Please enter a valid phone number with country code (e.g. +91-9876543210).");
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.signInWithOtp({
        phone: formattedPhone,
      });

      if (error) {
        setErrorMsg(extractAuthErrorMessage(error));
      } else {
        setOtpSent(true);
        setOtpNotice(
          `Verification OTP sent via SMS to ${formattedPhone}. Enter the 6-digit code below.`,
        );
      }
    } catch (err: unknown) {
      setErrorMsg(extractAuthErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg(null);

    const formattedPhone = formatPhoneNumber(phoneNum);

    if (!otpCode || otpCode.trim().length < 6) {
      setErrorMsg("Please enter the full 6-digit verification code sent to your phone.");
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.auth.verifyOtp({
        phone: formattedPhone,
        token: otpCode.trim(),
        type: "sms",
      });

      if (error) {
        setErrorMsg(extractAuthErrorMessage(error));
      } else if (data.session) {
        navigate({ to: redirectUrl as never });
      }
    } catch (err: unknown) {
      setErrorMsg(extractAuthErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="section-container pb-28 pt-24 md:pt-28 max-w-md mx-auto">
      <div className="text-center">
        <p className="text-sm font-semibold tracking-widest uppercase text-muted-foreground">Welcome Back</p>
        <h1 className="mt-4 font-display text-3xl sm:text-4xl text-foreground">Sign In</h1>
        <p className="mt-3 text-sm text-muted-foreground/80 leading-relaxed">
          Access your saved addresses, track orders, and view past purchases.
        </p>
      </div>

      <div className="mt-10 flex justify-center border-b border-border/40">
        <button
          type="button"
          onClick={() => {
            setAuthMethod("EMAIL");
            setErrorMsg(null);
          }}
          className={`px-8 py-3 text-sm transition-all ${
            authMethod === "EMAIL"
              ? "border-b-2 border-primary text-foreground font-medium"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          Email & Password
        </button>
        <button
          type="button"
          onClick={() => {
            setAuthMethod("PHONE");
            setErrorMsg(null);
          }}
          className={`px-8 py-3 text-sm transition-all ${
            authMethod === "PHONE"
              ? "border-b-2 border-primary text-foreground font-medium"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          Phone SMS OTP
        </button>
      </div>

      <div className="mt-8">
        {errorMsg && (
          <div className="mb-6 flex items-start gap-3 text-sm text-destructive bg-destructive/5 p-4 rounded-md border border-destructive/10">
            <AlertCircle className="h-5 w-5 shrink-0 text-destructive mt-0.5" />
            <span className="leading-relaxed">{errorMsg}</span>
          </div>
        )}

        {otpNotice && (
          <div className="mb-6 flex items-start gap-3 text-sm text-emerald-800 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/50 p-4 rounded-md border border-emerald-500/10">
            <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" />
            <span className="leading-relaxed">{otpNotice}</span>
          </div>
        )}

        {authMethod === "EMAIL" ? (
          <form onSubmit={handleEmailLogin} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-muted-foreground/70" />
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="customer@example.com"
                  className="h-12 w-full rounded-md border border-border/70 bg-background pl-11 pr-4 text-base transition-all hover:border-border focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary shadow-sm"
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label htmlFor="password" className="block text-sm font-medium text-foreground">
                  Password
                </label>
                <Link
                  to="/account/forgot-password"
                  className="text-sm text-muted-foreground hover:text-foreground underline transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-muted-foreground/70" />
                <input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-12 w-full rounded-md border border-border/70 bg-background pl-11 pr-4 text-base transition-all hover:border-border focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary shadow-sm"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="flex min-h-12 w-full items-center justify-center gap-2 rounded-md bg-primary text-sm font-semibold tracking-widest uppercase text-primary-foreground transition-all hover:opacity-90 hover:shadow-md disabled:opacity-50 disabled:hover:shadow-none mt-2"
            >
              {loading ? "Signing In..." : "Sign In"}
              <ArrowRight className="h-4 w-4" />
            </button>
          </form>
        ) : (
          /* Phone OTP Form */
          <div className="space-y-6">
            {!otpSent ? (
              <form onSubmit={handleSendOtp} className="space-y-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
                    Phone Number
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-muted-foreground/70" />
                    <input
                      id="phone"
                      type="tel"
                      required
                      value={phoneNum}
                      onChange={(e) => setPhoneNum(e.target.value)}
                      placeholder="+91 98765 43210"
                      className="h-12 w-full rounded-md border border-border/70 bg-background pl-11 pr-4 text-base transition-all hover:border-border focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary shadow-sm"
                    />
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground/80 leading-relaxed">
                    We will send a 6-digit SMS verification code to your mobile device.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="flex min-h-12 w-full items-center justify-center gap-2 rounded-md bg-primary text-sm font-semibold tracking-widest uppercase text-primary-foreground transition-all hover:opacity-90 hover:shadow-md disabled:opacity-50 disabled:hover:shadow-none"
                >
                  {loading ? "Sending OTP..." : "Send Code"}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOtp} className="space-y-6">
                <div>
                  <label htmlFor="otp" className="block text-sm font-medium text-foreground mb-2">
                    Verification Code
                  </label>
                  <div className="relative">
                    <KeyRound className="absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-muted-foreground/70" />
                    <input
                      id="otp"
                      type="text"
                      required
                      maxLength={6}
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value)}
                      placeholder="123456"
                      className="h-12 w-full rounded-md border border-border/70 bg-background pl-11 pr-4 font-mono text-center text-lg tracking-[0.4em] transition-all hover:border-border focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary shadow-sm"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="flex min-h-12 w-full items-center justify-center gap-2 rounded-md bg-primary text-sm font-semibold tracking-widest uppercase text-primary-foreground transition-all hover:opacity-90 hover:shadow-md disabled:opacity-50 disabled:hover:shadow-none"
                >
                  {loading ? "Verifying..." : "Verify & Sign In"}
                  <ArrowRight className="h-4 w-4" />
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setOtpSent(false);
                    setOtpNotice(null);
                  }}
                  className="w-full text-center text-sm text-muted-foreground hover:text-foreground underline transition-colors"
                >
                  Change phone number
                </button>
              </form>
            )}
          </div>
        )}

        <div className="mt-10 border-t border-border/40 pt-8 text-center">
          <p className="text-sm text-muted-foreground">
            Don't have an account?{" "}
            <Link
              to="/account/register"
              search={searchParams}
              className="text-foreground underline font-medium transition-colors hover:text-primary"
            >
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
